
import React, { useCallback, useEffect, useRef, useState } from "react";

import { DAYS, DAY_LABELS, createAsset, fetchAssetImage, readApiError } from "./printSignApi";

/*
 * Общие кусочки редакторов вывесок. Вид — как у «Каталогов под печать»
 * (классы printCatalogs__*), своё — в _printSigns.scss (printSigns__*).
 */

export const MAX_IMAGE_BYTES = 30 * 1024 * 1024;

export const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const formatBytes = (bytes) => {
    if (!(bytes > 0)) return "";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
};

export const sameDraft = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const Choice = ({ options, value, onChange, disabled }) => (
    <div className="printCatalogs__choices">
        {options.map((option) => (
            <button
                key={String(option.value)}
                type="button"
                className={`printCatalogs__choice${option.value === value ? " is-active" : ""}`}
                disabled={disabled}
                onClick={() => onChange(option.value)}
            >
                <strong>{option.label}</strong>
                {option.hint ? <span>{option.hint}</span> : null}
            </button>
        ))}
    </div>
);

export const Field = ({ label, value, onChange, placeholder, multiline, maxLength, disabled, type = "text" }) => (
    <label className="printCatalogs__field">
        <span>{label}</span>
        {multiline ? (
            <textarea value={value} placeholder={placeholder} maxLength={maxLength} disabled={disabled} rows={3}
                onChange={(event) => onChange(event.target.value)} />
        ) : (
            <input type={type} value={value} placeholder={placeholder} maxLength={maxLength} disabled={disabled}
                onChange={(event) => onChange(event.target.value)} />
        )}
    </label>
);

export const Section = ({ title, hint, children, actions }) => (
    <section className="printCatalogs__section">
        <div className="printCatalogs__sectionHead printSigns__sectionHead">
            <div>
                <h3>{title}</h3>
                {hint ? <p>{hint}</p> : null}
            </div>
            {actions || null}
        </div>
        {children}
    </section>
);

export const Switch = ({ checked, onChange, label, disabled }) => (
    <label className="printCatalogs__switch">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
        <span>{label}</span>
    </label>
);

/** Ползунок: подпись, значение и сброс двойным щелчком. */
export const Range = ({ label, value, min, max, step = 0.01, onChange, format, disabled, resetTo }) => (
    <label className="printSigns__range" onDoubleClick={() => resetTo !== undefined && onChange(resetTo)}>
        <span>
            {label}
            <em>{format ? format(value) : value}</em>
        </span>
        <input type="range" min={min} max={max} step={step} value={value} disabled={disabled}
            onChange={(event) => onChange(Number(event.target.value))} />
    </label>
);

/** Список строк: телефоны, почты, адреса. Сколько угодно, порядок меняется. */
export const StringList = ({ label, values, onChange, placeholder, max = 8, maxLength = 120, disabled, addLabel = "Добавить" }) => {
    const update = (index, value) => onChange(values.map((item, current) => (current === index ? value : item)));
    const remove = (index) => onChange(values.filter((_item, current) => current !== index));
    const move = (index, delta) => {
        const next = [...values];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <div className="printSigns__list">
            <div className="printCatalogs__label">{label}</div>
            {values.map((value, index) => (
                <div key={index} className="printSigns__listRow">
                    <input type="text" value={value} placeholder={placeholder} maxLength={maxLength} disabled={disabled}
                        onChange={(event) => update(index, event.target.value)} />
                    <button type="button" title="Выше" disabled={disabled || index === 0} onClick={() => move(index, -1)}>↑</button>
                    <button type="button" title="Ниже" disabled={disabled || index === values.length - 1} onClick={() => move(index, 1)}>↓</button>
                    <button type="button" title="Убрать" className="is-danger" disabled={disabled} onClick={() => remove(index)}>×</button>
                </div>
            ))}
            {values.length < max ? (
                <button type="button" className="printSigns__add" disabled={disabled} onClick={() => onChange([...values, ""])}>
                    + {addLabel}
                </button>
            ) : null}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Картинки, закрытые входом: забираем файлом и показываем с локального адреса */
/* ------------------------------------------------------------------ */

const imageCache = new Map();

/** Объектный URL картинки. key — уникальный ключ (меняется — картинка перезагружается). */
export const useAuthImage = (key, loader) => {
    const [url, setUrl] = useState(() => (key ? imageCache.get(key) || "" : ""));
    const loaderRef = useRef(loader);
    loaderRef.current = loader;

    useEffect(() => {
        if (!key) {
            setUrl("");
            return undefined;
        }
        if (imageCache.has(key)) {
            setUrl(imageCache.get(key));
            return undefined;
        }
        let alive = true;
        loaderRef.current()
            .then((blob) => {
                const objectUrl = URL.createObjectURL(blob);
                imageCache.set(key, objectUrl);
                if (alive) setUrl(objectUrl);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [key]);

    return url;
};

export const AssetThumb = ({ assetId, width = 360, className = "" }) => {
    const url = useAuthImage(assetId ? `asset:${assetId}:${width}` : "", () => fetchAssetImage(assetId, width));
    return (
        <span className={`printSigns__thumb ${className}`}>
            {url ? <img src={url} alt="" /> : <i>…</i>}
        </span>
    );
};

/** Иконка из справочника сервера (svg строкой). */
export const IconGlyph = ({ icon, meta }) => {
    const found = (meta?.icons || []).find((item) => item.key === icon);
    if (!found) return <span className="printSigns__glyph">★</span>;
    // eslint-disable-next-line react/no-danger
    return <span className="printSigns__glyph" dangerouslySetInnerHTML={{ __html: found.svg }} />;
};

/**
 * Выбор иконки: встроенная (линейная, красится под дизайн) или своя картинка из библиотеки.
 * value: { icon, assetId }.
 */
export const IconPicker = ({ value, onChange, meta, iconAssets = [], disabled }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const current = (meta?.icons || []).find((item) => item.key === value.icon);

    return (
        <div className="printSigns__iconPicker" ref={rootRef}>
            <button type="button" className="printSigns__iconButton" disabled={disabled} onClick={() => setOpen((state) => !state)}
                title={value.assetId ? "Своя иконка" : current?.label || "Иконка"}>
                {value.assetId ? <AssetThumb assetId={value.assetId} width={96} className="is-icon" /> : <IconGlyph icon={value.icon} meta={meta} />}
            </button>
            {open ? (
                <div className="printSigns__iconPopover">
                    <div className="printSigns__iconGrid">
                        {(meta?.icons || []).map((item) => (
                            <button key={item.key} type="button" title={item.label}
                                className={!value.assetId && value.icon === item.key ? "is-active" : ""}
                                onClick={() => {
                                    onChange({ icon: item.key, assetId: null });
                                    setOpen(false);
                                }}>
                                <IconGlyph icon={item.key} meta={meta} />
                            </button>
                        ))}
                    </div>
                    {iconAssets.length ? (
                        <>
                            <div className="printCatalogs__label">Свои иконки из библиотеки</div>
                            <div className="printSigns__iconGrid">
                                {iconAssets.map((asset) => (
                                    <button key={asset._id} type="button" title={asset.name}
                                        className={value.assetId === asset._id ? "is-active" : ""}
                                        onClick={() => {
                                            onChange({ icon: value.icon || "star", assetId: asset._id });
                                            setOpen(false);
                                        }}>
                                        <AssetThumb assetId={asset._id} width={96} className="is-icon" />
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="printCatalogs__hint">Свои иконки (PNG) загружаются во вкладке «Библиотека».</p>
                    )}
                </div>
            ) : null}
        </div>
    );
};

/**
 * Выбор картинки из библиотеки нужного вида + загрузка новой прямо отсюда.
 * value — assetId или null («без картинки» / «фон дизайна»).
 */
export const AssetPicker = ({ kind, assets, value, onChange, onUploaded, emptyLabel, disabled, setMessage }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const list = assets.filter((asset) => asset.kind === kind);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage?.({ type: "error", text: "Файл больше 30 МБ." });
            return;
        }
        setUploading(true);
        try {
            const asset = await createAsset({ kind, name: file.name.replace(/\.[^.]+$/, ""), file });
            onUploaded?.(asset);
            onChange(asset._id);
        } catch (error) {
            setMessage?.({ type: "error", text: await readApiError(error, "Не удалось загрузить картинку") });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="printSigns__assets">
            <button type="button" className={`printSigns__assetCard is-empty${!value ? " is-active" : ""}`} disabled={disabled}
                onClick={() => onChange(null)}>
                <span>{emptyLabel}</span>
            </button>
            {list.map((asset) => (
                <button key={asset._id} type="button" title={asset.name}
                    className={`printSigns__assetCard${value === asset._id ? " is-active" : ""}`}
                    disabled={disabled} onClick={() => onChange(asset._id)}>
                    <AssetThumb assetId={asset._id} />
                    <em>{asset.name}</em>
                </button>
            ))}
            <button type="button" className="printSigns__assetCard is-upload" disabled={disabled || uploading}
                onClick={() => inputRef.current?.click()}>
                <span>{uploading ? "Загрузка…" : "+ Загрузить"}</span>
            </button>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    upload(file);
                }} />
        </div>
    );
};

/** Настройки картинки в рамке: увеличение, сдвиг, затемнение. */
export const ImageAdjust = ({ value, onChange, disabled, children }) => (
    <div className="printSigns__adjust">
        <Range label="Увеличение" value={value.scale} min={1} max={4} step={0.05} disabled={disabled} resetTo={1}
            format={(number) => `×${Number(number).toFixed(2)}`} onChange={(scale) => onChange({ ...value, scale })} />
        <Range label="Сдвиг по горизонтали" value={value.offsetX} min={-1} max={1} step={0.02} disabled={disabled} resetTo={0}
            format={(number) => (Number(number) === 0 ? "центр" : `${Math.round(number * 100)}%`)} onChange={(offsetX) => onChange({ ...value, offsetX })} />
        <Range label="Сдвиг по вертикали" value={value.offsetY} min={-1} max={1} step={0.02} disabled={disabled} resetTo={0}
            format={(number) => (Number(number) === 0 ? "центр" : `${Math.round(number * 100)}%`)} onChange={(offsetY) => onChange({ ...value, offsetY })} />
        <Range label="Затемнение" value={value.darken} min={0} max={0.9} step={0.02} disabled={disabled} resetTo={0}
            format={(number) => `${Math.round(number * 100)}%`} onChange={(darken) => onChange({ ...value, darken })} />
        {children}
        <p className="printCatalogs__hint">Двойной щелчок по ползунку — вернуть как было.</p>
    </div>
);

/** Правая колонка: живой PNG-предпросмотр. */
export const PreviewPane = ({ url, loading, error, warnings, autoPreview, onAutoChange, onRefresh, title, subtitle, disabled }) => (
    <div className="printCatalogs__viewer printSigns__viewer">
        <div className="printCatalogs__viewerHead">
            <div>
                <strong>{title || "Предпросмотр"}</strong>
                <span>{subtitle || "Так вывеска выйдет в печать. Обновляется после правок."}</span>
            </div>
            <div className="printCatalogs__viewerTools">
                <Switch checked={autoPreview} onChange={onAutoChange} label="Обновлять сам" />
                <button type="button" disabled={loading || disabled} onClick={onRefresh}>{loading ? "Рисуем…" : "Обновить"}</button>
            </div>
        </div>

        {error ? <div className="printCatalogs__message is-error">{error}</div> : null}
        {warnings?.length ? (
            <div className="printCatalogs__message is-warning printSigns__warnings">
                {warnings.map((warning) => <div key={warning}>• {warning}</div>)}
            </div>
        ) : null}

        <div className={`printSigns__frame${loading ? " is-loading" : ""}`}>
            {url ? (
                <a href={url} target="_blank" rel="noreferrer" title="Открыть крупно">
                    <img src={url} alt="Предпросмотр вывески" />
                </a>
            ) : (
                <div className="printCatalogs__frameEmpty">{loading ? "Рисуем предпросмотр…" : "Нажмите «Обновить» — здесь появится вывеска."}</div>
            )}
        </div>
    </div>
);

/** Пауза после последней правки, прежде чем предпросмотр обновится сам. */
const AUTO_PREVIEW_MS = 900;

/**
 * Живой предпросмотр: request() возвращает { blob, warnings }.
 * deps — от чего зависит картинка (черновик); пока enabled и включено «Обновлять сам» —
 * после правок картинка перерисовывается сама. Показывается только последний ответ.
 */
export const usePreview = ({ request, deps, enabled }) => {
    const [state, setState] = useState({ url: "", loading: false, error: "", warnings: [], layout: null });
    const [auto, setAuto] = useState(true);
    const seqRef = useRef(0);
    const urlRef = useRef("");
    const requestRef = useRef(request);
    requestRef.current = request;

    const refresh = useCallback(async () => {
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const result = await requestRef.current();
            if (seq !== seqRef.current) return;
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
            const url = URL.createObjectURL(result.blob);
            urlRef.current = url;
            setState({ url, loading: false, error: "", warnings: result.warnings || [], layout: result.layout || null });
        } catch (error) {
            const text = await readApiError(error, "Не удалось нарисовать предпросмотр");
            if (seq !== seqRef.current) return;
            setState((current) => ({ ...current, loading: false, error: text }));
        }
    }, []);

    const reset = useCallback(() => {
        seqRef.current += 1;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = "";
        setState({ url: "", loading: false, error: "", warnings: [], layout: null });
    }, []);

    useEffect(() => () => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    }, []);

    const key = JSON.stringify(deps);
    useEffect(() => {
        if (!auto || !enabled) return undefined;
        const timer = window.setTimeout(() => refresh(), AUTO_PREVIEW_MS);
        return () => window.clearTimeout(timer);
    }, [auto, enabled, key, refresh]);

    return { ...state, auto, setAuto, refresh, reset };
};

/** Строки «иконка + текст» (услуги) или «иконка + цифра + текст» (преимущества). */
export const ItemsEditor = ({ items, onChange, meta, iconAssets, withValue, max, disabled, addLabel }) => {
    const update = (index, changes) => onChange(items.map((item, current) => (current === index ? { ...item, ...changes } : item)));
    const remove = (index) => onChange(items.filter((_item, current) => current !== index));
    const move = (index, delta) => {
        const next = [...items];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <div className="printSigns__items">
            {items.map((item, index) => (
                <div key={index} className={`printSigns__itemRow${withValue ? " has-value" : ""}`}>
                    <IconPicker value={item} meta={meta} iconAssets={iconAssets} disabled={disabled}
                        onChange={(icon) => update(index, icon)} />
                    {withValue ? (
                        <input type="text" className="printSigns__value" value={item.value} maxLength={24} disabled={disabled}
                            placeholder="1000+" onChange={(event) => update(index, { value: event.target.value })} />
                    ) : null}
                    <input type="text" value={item.text} maxLength={withValue ? 80 : 120} disabled={disabled}
                        placeholder={withValue ? "моделей памятников" : "Памятники из гранита"}
                        onChange={(event) => update(index, { text: event.target.value })} />
                    <button type="button" title="Выше" disabled={disabled || index === 0} onClick={() => move(index, -1)}>↑</button>
                    <button type="button" title="Ниже" disabled={disabled || index === items.length - 1} onClick={() => move(index, 1)}>↓</button>
                    <button type="button" title="Убрать" className="is-danger" disabled={disabled} onClick={() => remove(index)}>×</button>
                </div>
            ))}
            {items.length < max ? (
                <button type="button" className="printSigns__add" disabled={disabled}
                    onClick={() => onChange([...items, withValue ? { icon: "star", assetId: null, value: "", text: "" } : { icon: "star", assetId: null, text: "" }])}>
                    + {addLabel}
                </button>
            ) : null}
        </div>
    );
};

/** Кнопка «Импорт JSON»: выбрать файл и отдать его текст в onFile. */
export const JsonImportButton = ({ label = "Импорт JSON", disabled, onFile, className = "" }) => {
    const inputRef = useRef(null);
    return (
        <>
            <input ref={inputRef} type="file" accept="application/json,.json" hidden
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) onFile(file);
                }} />
            <button type="button" className={className} disabled={disabled} onClick={() => inputRef.current?.click()}>{label}</button>
        </>
    );
};

const DAY_MODES = [
    { value: "open", label: "Работаем" },
    { value: "closed", label: "Выходной" },
    { value: "allday", label: "Круглосуточно" },
];

/**
 * Время «ЧЧ:ММ» обычным полем: системное поле времени в части браузеров
 * показывает 12-часовой формат и не влезает в узкую колонку.
 */
const TimeInput = ({ value, onChange, disabled, placeholder }) => (
    <input
        type="text"
        inputMode="numeric"
        className="printSigns__time"
        value={value}
        maxLength={5}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => {
            const digits = event.target.value.replace(/[^\d]/g, "").slice(0, 4);
            onChange(digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits);
        }}
    />
);

/** Режим работы: строка на каждый день недели. */
export const ScheduleEditor = ({ schedule, onChange, disabled }) => {
    const update = (day, changes) => onChange({ ...schedule, [day]: { ...schedule[day], ...changes } });
    const copyMondayToWeekdays = () => onChange({
        ...schedule,
        ...["tue", "wed", "thu", "fri"].reduce((result, day) => ({ ...result, [day]: { ...schedule.mon } }), {}),
    });

    return (
        <div className="printSigns__schedule">
            <div className="printSigns__scheduleHead">
                <span>День</span><span>Как работаем</span><span>С</span><span>До</span><span>Перерыв с</span><span>до</span>
            </div>
            {DAYS.map((day) => {
                const value = schedule[day];
                const open = value.mode === "open";
                return (
                    <div key={day} className={`printSigns__scheduleRow${open ? "" : " is-off"}`}>
                        <strong>{DAY_LABELS[day]}</strong>
                        <select value={value.mode} disabled={disabled} onChange={(event) => update(day, { mode: event.target.value })}>
                            {DAY_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                        </select>
                        <TimeInput value={value.from} placeholder="10:00" disabled={disabled || !open} onChange={(from) => update(day, { from })} />
                        <TimeInput value={value.to} placeholder="18:00" disabled={disabled || !open} onChange={(to) => update(day, { to })} />
                        <TimeInput value={value.breakFrom} placeholder="—" disabled={disabled || !open} onChange={(breakFrom) => update(day, { breakFrom })} />
                        <TimeInput value={value.breakTo} placeholder="—" disabled={disabled || !open} onChange={(breakTo) => update(day, { breakTo })} />
                    </div>
                );
            })}
            <button type="button" className="printSigns__add" disabled={disabled} onClick={copyMondayToWeekdays}>
                Вт–Пт как понедельник
            </button>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* «Свои цвета»                                                        */
/* ------------------------------------------------------------------ */

const HEX_RE = /^#[0-9a-f]{6}$/i;

/**
 * Один цвет: готовые варианты кружками, своя палитра браузера и поле #RRGGBB.
 * allowAuto — есть вариант «Сам» (пустое значение: цвет подберёт сервер).
 */
export const ColorField = ({ label, value, palette = [], onChange, disabled, allowAuto, autoHint }) => {
    const [text, setText] = useState(value || "");
    useEffect(() => setText(value || ""), [value]);
    const commit = (raw) => {
        const clean = String(raw || "").trim().replace(/^([0-9a-f]{6})$/i, "#$1").toLowerCase();
        if (HEX_RE.test(clean)) onChange(clean);
        else if (allowAuto && !clean) onChange("");
        else setText(value || "");
    };
    return (
        <div className="signsColor">
            <div className="printCatalogs__label">{label}</div>
            <div className="signsColor__row">
                {allowAuto ? (
                    <button type="button" className={`signsColor__swatch is-auto${!value ? " is-active" : ""}`} disabled={disabled}
                        title={autoHint || "Подобрать само"} onClick={() => onChange("")}>
                        A
                    </button>
                ) : null}
                {palette.map((item) => (
                    <button key={item.value} type="button" title={item.label} disabled={disabled}
                        className={`signsColor__swatch${value === item.value ? " is-active" : ""}`}
                        style={{ background: item.value }}
                        onClick={() => onChange(item.value)} />
                ))}
                <label className="signsColor__picker" title="Свой цвет">
                    <input type="color" value={HEX_RE.test(value || "") ? value : "#808080"} disabled={disabled}
                        onChange={(event) => onChange(event.target.value)} />
                    <span style={{ background: HEX_RE.test(value || "") ? value : "transparent" }} />
                </label>
                <input type="text" className="signsColor__hex" value={text} maxLength={7} disabled={disabled}
                    placeholder={allowAuto ? "сам" : "#000000"}
                    onChange={(event) => setText(event.target.value)}
                    onBlur={(event) => commit(event.target.value)}
                    onKeyDown={(event) => event.key === "Enter" && commit(event.currentTarget.value)} />
            </div>
        </div>
    );
};

/** Тона дизайна «Свои цвета»: фон, металл, текст, фактура фона. */
export const ColorsEditor = ({ value, meta, onChange, disabled }) => {
    const palette = meta?.customColors?.palette || {};
    const textures = (meta?.customColors?.textures || []).map((item) => ({ value: item.key, label: item.label, hint: item.hint }));
    return (
        <div className="signsColors">
            <ColorField label="Фон (камень)" value={value.background} palette={palette.background} disabled={disabled}
                onChange={(background) => onChange({ background })} />
            <ColorField label="Металл — рамки, заголовки, кнопка" value={value.accent} palette={palette.accent} disabled={disabled}
                onChange={(accent) => onChange({ accent })} />
            <ColorField label="Текст" value={value.text} palette={[{ value: "#ffffff", label: "Белый" }, { value: "#1f1f22", label: "Тёмный" }]}
                allowAuto autoHint="Сам: светлый на тёмном фоне, тёмный на светлом" disabled={disabled}
                onChange={(text) => onChange({ text })} />
            {textures.length ? (
                <>
                    <div className="printCatalogs__label">Фактура фона</div>
                    <Choice options={textures} value={value.texture} disabled={disabled} onChange={(texture) => onChange({ texture })} />
                </>
            ) : null}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Кусок вывески или визитки: размер, сдвиг стрелками, показать/скрыть */
/* ------------------------------------------------------------------ */

const EyeIcon = ({ off }) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
        {off ? <path d="M3 3l18 18" /> : null}
    </svg>
);

/**
 * Строка куска: название, ползунок размера, стрелки ← → ↑ ↓ (сдвиг на шаг) и глаз (показать / скрыть).
 * onNudge(dx, dy) — dx, dy: −1, 0, 1. Скрытый кусок нельзя двигать и менять.
 */
export const PartRow = ({ label, scale, min = 0.3, max = 3, step = 0.05, onScale, onNudge, visible = true, onToggle, disabled, unavailable }) => {
    const off = !visible || unavailable;
    return (
        <div className={`signsPart${off ? " is-off" : ""}`}>
            <div className="signsPart__head">
                <strong>{label}</strong>
                <em>{unavailable || (!visible ? "скрыто" : `${Math.round(scale * 100)}%`)}</em>
            </div>
            <div className="signsPart__controls">
                <input type="range" min={min} max={max} step={step} value={scale} disabled={disabled || off}
                    onDoubleClick={() => onScale(1)} onChange={(event) => onScale(Number(event.target.value))} />
                <div className="signsPart__buttons">
                    <button type="button" title="Влево" disabled={disabled || off} onClick={() => onNudge(-1, 0)}>←</button>
                    <button type="button" title="Вправо" disabled={disabled || off} onClick={() => onNudge(1, 0)}>→</button>
                    <button type="button" title="Выше" disabled={disabled || off} onClick={() => onNudge(0, -1)}>↑</button>
                    <button type="button" title="Ниже" disabled={disabled || off} onClick={() => onNudge(0, 1)}>↓</button>
                    {onToggle ? (
                        <button type="button" className={`signsPart__eye${visible ? "" : " is-off"}`} title={visible ? "Скрыть" : "Показать"}
                            disabled={disabled || !!unavailable} onClick={() => onToggle(!visible)}>
                            <EyeIcon off={!visible} />
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
